import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const BROADCAST_API = process.env.REACT_APP_BROADCAST_SERVER;

interface Program {
    id: number;
    display_name: string;
}

interface BatchRecord {
    id: number;
    program_id: number;
    program_name: string;
    batch: number;
    total_student_count: number | null;
    actual_count: number;
    active_count: number;
    semi_verified_count: number;
}

const HandleBatch = () => {
    const { id: instituteId } = useParams<{ id: string }>();

    const [programs, setPrograms] = useState<Program[]>([]);
    const [batchList, setBatchList] = useState<BatchRecord[]>([]);
    const [yearOptions, setYearOptions] = useState<number[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const [form, setForm] = useState({
        program_id: '',
        batch: '',
        total_student_count: '',
    });

    const currentYear = new Date().getFullYear();

    useEffect(() => {
        if (!instituteId || instituteId === 'new') return;
        fetchAll();
    }, [instituteId]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [programsRes, foundingYearRes, listRes] = await Promise.all([
                axios.get(`${BROADCAST_API}/batch/programs/${instituteId}`),
                axios.get(`${BROADCAST_API}/batch/founding-year/${instituteId}`),
                axios.get(`${BROADCAST_API}/batch/list/${instituteId}`),
            ]);

            setPrograms(programsRes.data?.data || []);

            const foundingYear = foundingYearRes.data?.data?.founding_year;
            if (foundingYear) {
                const years: number[] = [];
                for (let y = Number(foundingYear); y <= currentYear; y++) {
                    years.push(y);
                }
                setYearOptions(years.reverse());
            }

            setBatchList(listRes.data?.data || []);
        } catch (err) {
            console.error('Error fetching batch data:', err);
        } finally {
            setLoading(false);
        }
    };

    const openModal = () => {
        setForm({ program_id: '', batch: '', total_student_count: '' });
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.program_id || !form.batch) {
            Swal.fire({ icon: 'warning', title: 'Validation', text: 'Program and Batch Year are required.' });
            return;
        }
        setSaving(true);
        try {
            const res = await axios.post(`${BROADCAST_API}/batch/save`, {
                institute_id: Number(instituteId),
                program_id: Number(form.program_id),
                batch: Number(form.batch),
                total_student_count: form.total_student_count !== '' ? Number(form.total_student_count) : null,
            });
            if (res.data?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Saved!',
                    text: 'Batch saved successfully.',
                    timer: 1500,
                    showConfirmButton: false,
                });
                setShowModal(false);
                fetchAll();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: res.data?.message || 'Failed to save batch.' });
            }
        } catch (err: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err?.response?.data?.message || 'Failed to save batch.',
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-success" role="status" />
            </div>
        );
    }

    return (
        <div className="p-4">

            {/* Header row */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="font-18 font-600 mb-0">Batch List</h5>
                <button className="btn btn-green" onClick={openModal}>
                    <span>+ Add New Batch</span>
                </button>
            </div>

            {/* Batch list table */}
            {batchList.length === 0 ? (
                <p className="text-muted text-center py-4">
                    No batches added yet. Click "Add New Batch" to get started.
                </p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle">
                        <thead style={{ backgroundColor: '#1a3c5e', color: '#fff' }}>
                            <tr>
                                <th>#</th>
                                <th>Program Name</th>
                                <th>Batch Year</th>
                                <th>Total Student Count</th>
                                <th>Active</th>
                                <th>Semi-Verified</th>
                            </tr>
                        </thead>
                        <tbody>
                            {batchList.map((b, idx) => (
                                <tr key={b.id}>
                                    <td>{idx + 1}</td>
                                    <td>{b.program_name}</td>
                                    <td>{b.batch}</td>
                                    <td>{b.total_student_count ?? '—'}</td>
                                    <td>
                                        <span className="badge bg-success">{b.active_count}</span>
                                    </td>
                                    <td>
                                        <span className="badge bg-warning text-dark">{b.semi_verified_count}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div
                    className="modal fade show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    tabIndex={-1}
                    role="dialog">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title font-18 font-600">Add New Batch</h5>
                                <button type="button" className="btn-close" onClick={closeModal} />
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">

                                    {/* Program */}
                                    <div className="mb-3">
                                        <label className="form-label font-14 font-500">
                                            Program <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            name="program_id"
                                            className="form-select"
                                            value={form.program_id}
                                            onChange={handleFormChange}
                                            required>
                                            <option value="">— Select Program —</option>
                                            {programs.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.display_name}
                                                </option>
                                            ))}
                                        </select>
                                        {programs.length === 0 && (
                                            <small className="text-danger">
                                                No programs found for this institute.
                                            </small>
                                        )}
                                    </div>

                                    {/* Batch Year */}
                                    <div className="mb-3">
                                        <label className="form-label font-14 font-500">
                                            Batch Year <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            name="batch"
                                            className="form-select"
                                            value={form.batch}
                                            onChange={handleFormChange}
                                            required>
                                            <option value="">— Select Year —</option>
                                            {yearOptions.map((y) => (
                                                <option key={y} value={y}>
                                                    {y}
                                                </option>
                                            ))}
                                        </select>
                                        {yearOptions.length === 0 && (
                                            <small className="text-danger">
                                                Founding year not configured for this institute.
                                            </small>
                                        )}
                                    </div>

                                    {/* Total Student Count */}
                                    <div className="mb-3">
                                        <label className="form-label font-14 font-500">
                                            Total Batch Student Count
                                        </label>
                                        <input
                                            type="number"
                                            name="total_student_count"
                                            className="form-control"
                                            placeholder="Enter total student count"
                                            min={0}
                                            value={form.total_student_count}
                                            onChange={handleFormChange}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-green"
                                        disabled={saving}>
                                        <span>{saving ? 'Saving…' : 'Save Batch'}</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HandleBatch;
